import { createContext, useContext } from "react";

export const LINK_VERIFIER_PENDING_CLASS = "_pending_verification";
export const LINK_VERIFIER_BROKEN_CLASS = "_broken_link";

export type LinkVerifierStatus = "pending" | "broken" | "ok" | "off";

export interface LinkVerifierContextValue {
    register: (instanceId: string, path: string) => void;
    unregister: (instanceId: string) => void;
    getStatus: (path: string) => LinkVerifierStatus;
}

export const LinkVerifierContext = createContext<LinkVerifierContextValue | null>(null);

export function useLinkVerifier(): LinkVerifierContextValue | null {
    return useContext(LinkVerifierContext);
}

/**
 * Merge verifier markers onto an author class string. Status `null` leaves the
 * author value unchanged (not a candidate / no verifier). Status `"ok"` and
 * `"off"` add no class (pending/broken are stripped).
 */
export function mergeLinkVerifierClassName(
    authorClassName: string | undefined,
    status: LinkVerifierStatus | null,
): string | undefined {
    if (status === null) {
        return authorClassName;
    }
    const tokens = (authorClassName ?? "")
        .split(/\s+/)
        .filter((token) =>
            token
            && token !== LINK_VERIFIER_PENDING_CLASS
            && token !== LINK_VERIFIER_BROKEN_CLASS,
        );
    if (status === "pending") {
        tokens.push(LINK_VERIFIER_PENDING_CLASS);
    } else if (status === "broken") {
        tokens.push(LINK_VERIFIER_BROKEN_CLASS);
    }
    return tokens.length > 0 ? tokens.join(" ") : undefined;
}
